import { ModalService } from "./services/modal.service";
import { useRouter } from "next/router";
import { HStack, VStack } from "./composition/stack.layout";
import Buttons from "./button";
import { useEffect, useState } from "react";
import { useBeforeUnload } from "react-use";

interface LeaveConfirmationProps {
  message: string;
  destinationRoute: string;
  setLeaveConfirmed: any;
}

const LeaveConfirmationModal = (props: LeaveConfirmationProps) => {
  const router = useRouter();
  return (
    <div className="w-full max-w-2xl px-6 md:px-0">
      <VStack className="relative rounded-lg bg-white px-6 text-xl">
        <HStack className="items-center space-x-4 py-6">{props.message}</HStack>
        <HStack className={"items-center justify-end space-x-2 py-6"}>
          <Buttons.Standard
            variant={"secondary"}
            onClick={() => {
              ModalService.closeModal();
            }}
          >
            Cancel
          </Buttons.Standard>
          <Buttons.Standard
            variant={"primary-magenta"}
            onClick={async () => {
              await props.setLeaveConfirmed(true);
              await ModalService.closeModal();
              await router.push(props.destinationRoute);
            }}
          >
            Confirm
          </Buttons.Standard>
        </HStack>
      </VStack>
    </div>
  );
};

// Handles the confirmation modal for leaving the page
// also handles window onbeforeunload
export const useLeaveConfirmation = (opts: {
  message: string;
  destinationRoute: string;
  enabled: boolean;
}) => {
  const router = useRouter();
  const [leaveConfirmed, setLeaveConfirmed] = useState(false);
  useBeforeUnload(opts.enabled, "Are you sure you want to leave this page?");

  useEffect(() => {
    if (opts.enabled) {
      const handler = (e) => {
        if (e !== window.location.pathname && !leaveConfirmed) {
          ModalService.openModal(() => (
            <LeaveConfirmationModal
              message={opts.message}
              destinationRoute={opts.destinationRoute}
              setLeaveConfirmed={(value) => {
                setLeaveConfirmed(value);
              }}
            />
          ));
          router.events.emit("routeChangeError");
          throw "Abort route change. Please ignore this error.";
        }
      };
      router.events.on("beforeHistoryChange", handler);
      return () => {
        router.events.off("beforeHistoryChange", handler);
      };
    }
  }, [opts.enabled, opts.message, opts.destinationRoute, leaveConfirmed]);
};

export default function useConfirmLeaveGuard(
  message: string,
  defaultDestinationRoute: string,
): any {
  const [isBlocked, setIsBlocked] = useState(false);
  const [leaveConfirmed, setLeaveConfirmed] = useState(false);
  const [destinationRoute, setDestinationRoute] = useState(
    defaultDestinationRoute,
  );
  const router = useRouter();

  // route guard to stop user from navigating away with buckets in editing state
  useEffect(() => {
    const handleRouteChange = () => {
      if (leaveConfirmed) {
        return;
      }

      ModalService.openModal(() => (
        <LeaveConfirmationModal
          message={message}
          destinationRoute={destinationRoute}
          setLeaveConfirmed={(value) => setLeaveConfirmed(value)}
        />
      ));

      router.events.emit("routeChangeError");
      history.replaceState(null, null, router.basePath + router.asPath);
      throw "Abort route change. Please ignore this error.";
    };

    if (isBlocked) {
      router.events.on("routeChangeStart", handleRouteChange);
    }

    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, [isBlocked, leaveConfirmed, message]);

  return [isBlocked, setIsBlocked, destinationRoute, setDestinationRoute];
}
